import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import useIsMobile from "../../hooks/useIsMobile";
import {
  TRACKER_COLORS as C,
  MONO,
  PRIORITY_STYLES,
  formatShortDate,
} from "../../constants/trackerUi";
import { DayMeter } from "./StagePipeline";
import StagePanel from "./StagePanel";
import CompletionReport from "./CompletionReport";
import PromoteChecklistItem from "./PromoteChecklistItem";
import { useToggleChecklistItem } from "../../api/trackerApi";

const KIND_ICON = {
  accepted: "✓",
  force_started: "▶",
  force_completed: "⇥",
  completed: "→",
  blocked: "⛔",
  unblocked: "▲",
  rejected: "✕",
  rollout: "◐",
  raised: "＋",
  reassigned: "⇄",
  comment: "💬",
  skipped: "⤻",
  edited: "✎",
};
const KIND_COLOR = {
  accepted: C.done,
  force_started: C.done,
  force_completed: C.accent,
  completed: C.accent,
  blocked: C.warn,
  rejected: C.danger,
  reassigned: C.accent,
  raised: C.inkFaint,
  skipped: C.inkFaint,
  edited: C.inkFaint,
};

function fmtDate(v) {
  const s = formatShortDate(v);
  return s || null;
}

/**
 * What actually happened, in words.
 *
 * Everything but a plain comment is a system event with no body text — the
 * meaning lives in `kind` and `details`, so without this the log was just a
 * column of names.
 */
function describeUpdate(u) {
  const d = u.details || {};
  const stage = u.stage_label || u.stage_code;
  const eta = fmtDate(d.etaDate);

  switch (u.kind) {
    case "raised":
      return (
        <>
          raised this requirement
          {u.owner_name ? <> and handed <b>{stage}</b> to <b>{u.owner_name}</b></> : null}
          {eta ? <> — target {eta}</> : null}
        </>
      );
    case "accepted": {
      const moved = d.previousEta && d.etaDate && d.previousEta !== d.etaDate;
      return (
        <>
          accepted <b>{stage}</b>
          {moved ? (
            <> and moved the date from {fmtDate(d.previousEta)} to {eta}</>
          ) : eta ? (
            <> — due {eta}</>
          ) : null}
        </>
      );
    }
    case "force_started":
      // Deliberately not worded like "accepted" — the owner didn't do this,
      // a steward started it on their behalf (see canForceStartStage).
      return (
        <>
          started <b>{stage}</b>
          {u.owner_name ? <> on <b>{u.owner_name}</b>'s behalf</> : null}
          {eta ? <> — due {eta}</> : null}
        </>
      );
    case "force_completed":
      // Deliberately not "completed": the owner did not finish this, someone
      // moved the requirement past it (see canForceStartStage).
      return (
        <>
          marked <b>{stage}</b> completed
          {d.toLabel ? <> — moved the requirement to <b>{d.toLabel}</b></> : null}
          {u.to_owner_name ? <> for <b>{u.to_owner_name}</b></> : null}
        </>
      );
    case "skipped":
      /* Recorded per bypassed stage, so the log says which steps were never
         done rather than leaving an unexplained gap in the pipeline. */
      return (
        <>
          skipped <b>{stage}</b>
        </>
      );
    case "edited":
      // What changed is in u.body, rendered by the shared "reason" callout
      // below (the same box blocked/rejected use) — no need to repeat it here.
      return <>edited this requirement</>;
    case "completed":
      return (
        <>
          completed <b>{stage}</b>
          {Array.isArray(d.skippedStages) && d.skippedStages.length ? (
            <> (skipping {d.skippedStages.length} stage{d.skippedStages.length > 1 ? "s" : ""})</>
          ) : null}
          {d.nextStage && u.to_owner_name ? (
            <> and handed it to <b>{u.to_owner_name}</b></>
          ) : d.nextStage ? (
            <> and moved it on</>
          ) : (
            <> — the requirement is closed</>
          )}
          {d.nextEta ? <> — target {fmtDate(d.nextEta)}</> : null}
        </>
      );
    case "reassigned":
      return (
        <>
          moved <b>{stage}</b>
          {u.from_owner_name ? <> from <b>{u.from_owner_name}</b></> : null}
          {u.to_owner_name ? <> to <b>{u.to_owner_name}</b></> : null}
          {eta ? <> — target {eta}</> : null}
        </>
      );
    case "blocked":
      return <>flagged <b>{stage}</b> as blocked</>;
    case "unblocked":
      return <>cleared the block on <b>{stage}</b></>;
    case "rejected":
      return (
        <>
          sent <b>{stage}</b> back
          {d.returnedTo ? <> to the previous stage</> : null}
        </>
      );
    case "rollout":
      return (
        <>
          set <b>{stage}</b> rollout to {d.rolloutPercent}%
          {d.versionLabel ? <> ({d.versionLabel})</> : null}
        </>
      );
    default:
      return <>commented on <b>{stage}</b></>;
  }
}

/** The activity trail. Kept behind its own toggle so it never crowds the card. */
function UpdateFeed({ updates }) {
  if (!updates?.length) {
    return <div style={{ fontSize: "0.78rem", color: C.inkFaint, padding: "10px 0" }}>Nothing logged yet.</div>;
  }
  const kindIcon = KIND_ICON;
  const kindColor = KIND_COLOR;

  return (
    <div>
      {updates.map((u) => (
        <div
          key={u.id}
          style={{
            display: "grid",
            gridTemplateColumns: "22px 1fr auto",
            gap: 10,
            padding: "9px 0",
            borderBottom: `1px solid ${C.lineSoft}`,
            fontSize: "0.78rem",
            alignItems: "start",
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.6rem",
              fontWeight: 900,
              color: "#fff",
              background: kindColor[u.kind] || C.upcoming,
            }}
          >
            {kindIcon[u.kind] || "💬"}
          </span>
          <div>
            <div style={{ color: C.inkSoft }}>
              <b style={{ color: C.ink }}>{u.author_name || "Someone"}</b> {describeUpdate(u)}
            </div>
            {/* The reason or note the person actually typed. */}
            {u.body ? (
              <div
                style={{
                  marginTop: 4,
                  paddingLeft: 9,
                  borderLeft: `2px solid ${C.line}`,
                  color: C.inkSoft,
                }}
              >
                {u.body}
              </div>
            ) : null}
            {u.links?.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 5 }}>
                {u.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      textDecoration: "none",
                      background: C.sunken,
                      border: `1px solid ${C.line}`,
                      borderRadius: 8,
                      padding: "5px 9px",
                      fontSize: "0.74rem",
                      color: C.accentInk,
                      fontWeight: 600,
                    }}
                  >
                    🔗 <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label || l.url}</span>
                  </a>
                ))}
              </div>
            ) : null}
          </div>
          <span style={{ fontFamily: MONO, fontSize: "0.64rem", color: C.inkFaint, whiteSpace: "nowrap" }}>
            {formatShortDate(u.created_at)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One requirement. Collapsed it's a single dense row; "More" opens the detail
 * and the stage controls; the full log sits behind one further toggle, so the
 * common case — scanning what's moving — stays compact.
 */
/**
 * @param {boolean} autoFocus   Scroll to and highlight this card.
 * @param {boolean} openOnFocus Also expand it. A notification deep-link opens
 *   the thing you clicked through to; a card you just created should stay
 *   collapsed like the rest of the list, so the list still reads as a list.
 */
export default function RequirementCard({
  requirement,
  autoFocus = false,
  openOnFocus = false,
  usesSprints = false,
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(autoFocus && openOnFocus);
  const [showLog, setShowLog] = useState(false);
  const [promoteItem, setPromoteItem] = useState(null);
  const toggleItem = useToggleChecklistItem();
  const cardRef = useRef(null);

  // Linked to from My Tasks or a notification: bring the card into view once,
  // so arriving on a long list doesn't leave the target below the fold.
  useEffect(() => {
    if (!autoFocus || !cardRef.current) return;
    cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [autoFocus]);

  const current = requirement.currentStage;
  const stages = requirement.stages || [];
  const idx = stages.findIndex((s) => s.stageCode === requirement.stageCode);
  const nextStage = idx >= 0 && idx < stages.length - 1 ? stages[idx + 1] : null;
  const isDone = requirement.state === "completed";
  const pri = PRIORITY_STYLES[requirement.priority] || PRIORITY_STYLES.Low;

  const doneCount = (requirement.checklist || []).filter((c) => c.done).length;

  /**
   * The row flags that something is waiting on you and opens the card; it no
   * longer accepts on the spot. Accepting now commits a date *and* possibly a
   * sprint, so it belongs where both controls are visible — a one-click accept
   * from the row would silently skip the sprint choice.
   */
  const awaitingMe = !isDone && current?.isMine && current?.status === "pending_accept";

  const openForReview = (e) => {
    e.stopPropagation(); // the row itself toggles open
    setOpen(true);
  };

  return (
    <div
      ref={cardRef}
      style={{
        background: C.surface,
        // A linked-to card gets the accent border so it's obvious which one
        // you were sent to, even in a list where several look alike.
        border: `1px solid ${
          current?.status === "blocked" ? C.warn : autoFocus ? C.accent : C.line
        }`,
        borderRadius: 13,
        overflow: "hidden",
        // Settles to nothing, so the card looks ordinary once you have found
        // it. Respects prefers-reduced-motion via the keyframes below.
        animation: autoFocus ? "trackerCardArrive 1.6s ease-out 1" : undefined,
      }}
    >
      {autoFocus ? (
        <style>{`
          @keyframes trackerCardArrive {
            0%   { box-shadow: 0 0 0 0 ${C.accent}00; }
            15%  { box-shadow: 0 0 0 4px ${C.accent}59; }
            60%  { box-shadow: 0 0 0 4px ${C.accent}33; }
            100% { box-shadow: 0 0 0 0 ${C.accent}00; }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes trackerCardArrive {
              0%, 100% { box-shadow: 0 0 0 3px ${C.accent}40; }
            }
          }
        `}</style>
      ) : null}
      {/* compact row */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => e.key === "Enter" && setOpen((v) => !v)}
        style={{
          display: "grid",
          // Title, then the meter, then owner / priority / action. The meter
          // gets the wider share because a squeezed progress bar is unreadable,
          // while a title can ellipsis gracefully.
          //
          // Those 190px + 120px floors add up to more than a phone has, so on
          // mobile the row becomes a stack instead of overflowing.
          gridTemplateColumns: isMobile
            ? "1fr"
            : `minmax(0,1fr) minmax(190px,1.3fr) minmax(120px,auto) auto${
                awaitingMe ? " auto" : ""
              }`,
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? 10 : 16,
          padding: "12px 15px",
          cursor: "pointer",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "0.86rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {requirement.title}
          </div>
          {/* The owner has its own column — repeating it here just crowds the
              line. Keep this for the stage and anything exceptional. */}
          <div
            style={{
              fontSize: "0.7rem",
              color: current?.status === "blocked" ? C.warn : C.inkFaint,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {isDone ? "Closed" : current?.label}
            {current?.status === "blocked" && current?.blockedReason ? ` · ${current.blockedReason}` : ""}
          </div>
        </div>

        {isDone ? (
          <div style={{ fontSize: "0.71rem", color: C.done, fontWeight: 700, textAlign: "center" }}>Completed</div>
        ) : (
          <DayMeter stage={current} stages={stages} compact />
        )}

        <div style={{ minWidth: 0, textAlign: "right" }}>
          <div
            style={{
              fontSize: "0.76rem",
              fontWeight: 700,
              color: C.inkSoft,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={current?.ownerName || ""}
          >
            {current?.ownerName || "—"}
          </div>
          {current?.ownerRole ? (
            <div style={{ fontSize: "0.62rem", color: C.inkFaint, fontWeight: 500 }}>{current.ownerRole}</div>
          ) : null}
        </div>

        <span
          style={{
            fontSize: "0.66rem",
            fontWeight: 800,
            padding: "3px 9px",
            borderRadius: 100,
            background: pri.bg,
            color: pri.color,
            whiteSpace: "nowrap",
          }}
        >
          {requirement.priority}
        </span>

        {awaitingMe ? (
          <button
            onClick={openForReview}
            title={
              current.etaDate || current.suggestedEtaDate
                ? `Review and accept — proposed for ${formatShortDate(current.etaDate || current.suggestedEtaDate)}`
                : "Review and accept"
            }
            style={{
              border: "none",
              background: C.done,
              color: "#fff",
              borderRadius: 8,
              padding: "6px 13px",
              fontSize: "0.76rem",
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {open ? "Review below ↓" : "Open to accept"}
          </button>
        ) : null}
      </div>

      {/* level 1 — More */}
      <div style={{ borderTop: `1px solid ${C.lineSoft}` }}>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            padding: "8px 15px",
            cursor: "pointer",
            fontSize: "0.7rem",
            fontWeight: 700,
            color: C.inkFaint,
          }}
        >
          {open ? "Less ▴" : "More — detail, checklist & actions ▾"}
        </button>
      </div>

      {open ? (
        <div style={{ borderTop: `1px solid ${C.line}`, background: C.sunken, padding: "16px" }}>
          {requirement.description ? (
            <p style={{ fontSize: "0.83rem", color: C.inkSoft, margin: "0 0 12px", maxWidth: "74ch" }}>
              {requirement.description}
            </p>
          ) : null}

          {requirement.checklist?.length ? (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: MONO, fontSize: "0.6rem", textTransform: "uppercase", color: C.inkFaint, marginBottom: 7 }}>
                Checklist — {doneCount}/{requirement.checklist.length}
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {requirement.checklist.map((item) => (
                  <li key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: "0.8rem" }}>
                    <input
                      type="checkbox"
                      checked={item.done}
                      onChange={(e) =>
                        toggleItem
                          .mutateAsync({ id: requirement.id, itemId: item.id, done: e.target.checked })
                          .catch((err) => toast.error(err.response?.data?.message || "Could not update"))
                      }
                      style={{ marginTop: 2, cursor: "pointer" }}
                    />
                    <span style={{ flex: 1, minWidth: 0, color: item.done ? C.inkFaint : C.ink, textDecoration: item.done ? "line-through" : "none" }}>
                      {item.label}
                    </span>
                    {item.entry_id ? (
                      <span
                        title="Already in the Daily Notebook"
                        style={{ fontFamily: MONO, fontSize: "0.58rem", color: C.done, whiteSpace: "nowrap" }}
                      >
                        in notebook
                      </span>
                    ) : !item.done && requirement.epicIssueId ? (
                      <button
                        type="button"
                        onClick={() => setPromoteItem(item)}
                        title="Make this someone's task for the day"
                        style={{
                          border: `1px solid ${C.line}`,
                          background: C.surface,
                          color: C.inkSoft,
                          borderRadius: 100,
                          padding: "1px 9px",
                          fontSize: "0.66rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        + notebook
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {promoteItem ? (
            <PromoteChecklistItem
              requirement={requirement}
              item={promoteItem}
              onClose={() => setPromoteItem(null)}
            />
          ) : null}

          {!isDone && current ? (
            <StagePanel
              requirement={requirement}
              stage={current}
              nextStage={nextStage}
              usesSprints={usesSprints}
            />
          ) : null}

          {/* How it ran against the department's standard — a live scorecard
              while in flight, the closing report once it's done. */}
          <CompletionReport requirement={requirement} />

          {/* level 2 — full log */}
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => setShowLog((v) => !v)}
              style={{
                border: `1px dashed ${C.line}`,
                background: C.surface,
                color: C.inkFaint,
                padding: "6px 16px",
                borderRadius: 100,
                fontSize: "0.7rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {showLog ? "Hide activity log ▴" : `Show activity log (${requirement.updates?.length || 0}) ▾`}
            </button>
          </div>

          {showLog ? (
            <div style={{ marginTop: 12, background: C.surface, border: `1px solid ${C.line}`, borderRadius: 12, padding: "4px 14px" }}>
              <UpdateFeed updates={requirement.updates} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
